import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { AdminLayout } from './components/layout/AdminLayout';
import { UserLayout } from './components/layout/UserLayout';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import TrainingSession from './pages/TrainingSession';
import AdminPanel from './pages/AdminPanel';

function AppRoutes() {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen text="Loading application..." />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Admin and Manager routes - Full admin interface with learner view access
  if (user?.role === 'admin' || user?.role === 'manager') {
    return (
      <Routes>
        {/* Admin Layout Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredPermission="view_analytics">
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminPanel />} />
          <Route path="analytics" element={<AdminPanel />} />
          <Route path="users" element={<AdminPanel />} />
          <Route path="courses" element={<AdminPanel />} />
          <Route path="courses/builder" element={<AdminPanel />} />
          <Route path="courses/library" element={<AdminPanel />} />
          <Route path="certificates" element={<AdminPanel />} />
          <Route path="reports" element={<AdminPanel />} />
          <Route path="scheduling" element={<AdminPanel />} />
          <Route path="settings" element={<AdminPanel />} />
        </Route>

        {/* Learner View Routes (for admin/manager to see learner experience) */}
        <Route path="/learner" element={<UserLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="training/:courseId" element={<TrainingSession />} />
        </Route>

        {/* Default redirect to admin for admin/manager users */}
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }

  // Trainer routes - Limited admin access + learner view
  if (user?.role === 'trainer') {
    return (
      <Routes>
        {/* Trainer Admin Layout Routes (limited access) */}
        <Route path="/admin" element={
          <ProtectedRoute requiredPermission="view_courses">
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminPanel />} />
          <Route path="analytics" element={<AdminPanel />} />
          <Route path="courses" element={<AdminPanel />} />
          <Route path="courses/builder" element={<AdminPanel />} />
          <Route path="courses/library" element={<AdminPanel />} />
          <Route path="certificates" element={<AdminPanel />} />
          <Route path="reports" element={<AdminPanel />} />
        </Route>

        {/* Learner Layout Routes (trainer's own learning) */}
        <Route path="/" element={<UserLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="training/:courseId" element={<TrainingSession />} />
        </Route>

        {/* Default redirect to learner dashboard for trainers */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Regular driver/learner routes - Only learner interface
  return (
    <Routes>
      <Route path="/" element={<UserLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="training/:courseId" element={<TrainingSession />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <AppRoutes />
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;